import React from 'react';
import {
	LanguageWrapper,
	SocketProvider,
	APPLICATION_NAMES,
	SCOPES,
} from '@techchange/components';
import {
	loadTranslations as loadComponentsTranslations,
} from '@techchange/components/src/_shared/i18n/translations/index.js';
import PropTypes from 'prop-types';
import { renderRoutes } from 'react-router-config';
import { useLocation } from 'react-router-dom';

// import AppErrorPage, { ERROR_PAGE_STATUS_CODE_SITE_OUTAGE } from './AppErrorPage';

import { AUTHENTICATION_STATES } from '../shared/constants/auth';
import { useAppContext } from '../shared/contexts/AppContext';
import { PostRegistrationWrapper } from '../shared/contexts/PostRegistrationContext';
import useGoogleAnalytics from '../shared/hooks/useGoogleAnalytics';

import routes from './routes';

// Vite resolves import.meta.glob statically, so each language becomes its own
// lazily-loaded chunk in both the client and SSR bundles. A template-string
// `import(\`…/${language}.json\`, { with: { type: 'json' } })` is left alone by
// Vite's dynamic-import rewriting and resolves relative to the built bundle at
// runtime instead: a 404 in the browser, ERR_MODULE_NOT_FOUND on the server.
const appTranslationModules = import.meta.glob([
	'../shared/i18n/translations/*.json',
	'!**/*_OLD.json',
]);

const propTypes = {

	/**
	 * The preloaded data required for SSR to render the AppWrapper component.
	 */
	preloadedData: PropTypes.shape({

		/**
		 * The preferred language for the user.
		 */
		preferredLanguage: PropTypes.string.isRequired,

		/**
		 * The translated message files to import for use with the react-intl library.
		 */
		messages: PropTypes.shape({}),

	}),

};

const AppWrapper = ({ preloadedData }) => {

	const {
		currentUser,
		authState,
		myOrganization,
		myOrganizationError,
	} = useAppContext();

	const { pathname } = useLocation();

	// Initialize Google Analytics
	useGoogleAnalytics(pathname, myOrganization);

	// If there was an error fetching the organization, render the error page...
	if (myOrganizationError) {

		// TODO: Add error page
		return null;

	}

	// Check if we're still loading the organization...
	if (!myOrganization) {
		return null;
	}

	// Don't render until the user authentication check is done...
	if (authState === AUTHENTICATION_STATES.PENDING ||
		authState === AUTHENTICATION_STATES.UNINITIALIZED) {
		return null;
	}

	return (
		<LanguageWrapper
			currentUser={currentUser}
			myOrganization={myOrganization}
			preloadedData={preloadedData}
			selectAppLanguageFiles={selectAppLanguageFiles}>
			<SocketProvider
				appName={APPLICATION_NAMES.PRINCEGE}
				scope={SCOPES.ORGANIZATION}
				scopeId={myOrganization?.id}>
				<PostRegistrationWrapper
					currentUser={currentUser}
					myOrganization={myOrganization}>
					{renderRoutes(routes)}
				</PostRegistrationWrapper>
			</SocketProvider>
		</LanguageWrapper>
	);

};

AppWrapper.propTypes = propTypes;

export default AppWrapper;

/**
 * Selects the translated messages file given the chosen language for the App.
 *
 * @param {string} language  The chosen language to use to render the App.
 *
 * @returns {Promise<Object>} result  The translated messages to use.
 */
export async function selectAppLanguageFiles(language) {

	try {

		// Parallelize loading of the translation data.
		const loadAppTranslations = appTranslationModules[
			`../shared/i18n/translations/${language}.json`
		];

		const result = await Promise.all([
			loadAppTranslations ? loadAppTranslations() : {},
			loadComponentsTranslations(language),
		]);

		return { ...(result[0].default || result[0]), ...result[1] };

	} catch (error) {
		console.error(error);
	}

	return {};

}
